import { ReactNode } from 'react';
import MUITable from '@mui/material/Table';
import Box from '@mui/material/Box';
import TableBody from '@mui/material/TableBody';
import TableSortLabel from '@mui/material/TableSortLabel';
import TableCell, { TableCellProps } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Pagination from '@mui/material/Pagination';
import Typography from '../Typography';
import { COLORS } from '../../theme';
import SelectField from '../Fields/SelectField';

type Nullable<T> = T | null;

export type ColumnField = {
  field?: string;
  headerName: string;
  width?: number;
  renderCell?: (params: any) => React.ReactElement;
  headerAlign?: TableCellProps['align'];
  justifyContentLastColumn?: string;
  sortable?: boolean;
};

type PaginationSettingsProps = {
  handlePageChange: (event: any, newPage: number) => void;
  page: number;
  rowsPerPage?: number;
  rowsPerPageOptions?: number[];
  setRowsPerPage?: (rows: number) => void;
};

export type PageNumPaginationSettings = PaginationSettingsProps & {
  count: number;
  showPages?: boolean;
};

export type InfinitePaginationSettings = PaginationSettingsProps & {
  firstPage?: number;
  hasMore: boolean;
};

export type PaginationSettings = PageNumPaginationSettings | InfinitePaginationSettings;

// Texts of the numbered paginator (showPages). The lib does not translate them.
export type PaginationLabels = {
  rowsPerPage?: ReactNode;
  ofAmount?: ReactNode;
};

const DEFAULT_PAGINATION_LABELS = { rowsPerPage: 'Rows per page', ofAmount: 'of' };

export type CustomTableProps = {
  columns: ColumnField[];
  data: any[];
  paginationSettings?: Nullable<PaginationSettings>;
  isLoading?: boolean;
  onClickRow?: Nullable<(row: any) => void>;
  emptyMessageTitle?: string;
  emptyMessageSubtitle?: string;
  emptyMessageIcon?: ReactNode;
  minWidth?: number;
  onClickSort?: Nullable<(field: string) => void>;
  sortingDirection?: 'asc' | 'desc';
  sortingField?: string;
  stickyHeaders?: boolean;
  // Optional per-row style overrides (e.g. dim non-actionable rows). Merged into the row sx.
  rowSx?: (row: any) => object;
  // Hide the trailing hover chevron (use when onClickRow is a select/toggle, not "open details").
  hideRowChevron?: boolean;
  paginationLabels?: PaginationLabels;
};

export const isPageNumPaginationSettings = (
  paginationSettings: PaginationSettings
): paginationSettings is PageNumPaginationSettings => {
  return (paginationSettings as PageNumPaginationSettings).count !== undefined;
};

const PaginationComponent = ({
  paginationSettings,
  isLoading,
  labels,
}: {
  paginationSettings: PaginationSettings;
  isLoading: boolean;
  labels: PaginationLabels;
}) => {
  // PageNumPaginationSettings
  const DEFAULT_ROWS_PER_PAGE = 10;
  if (isPageNumPaginationSettings(paginationSettings)) {
    const {
      count,
      showPages,
      handlePageChange,
      page,
      rowsPerPage = DEFAULT_ROWS_PER_PAGE,
      setRowsPerPage,
      rowsPerPageOptions = [10],
    } = paginationSettings;
    if (count === 0) {
      return null;
    }
    if (showPages) {
      // If showPages, it returns a paginator with "Number of records: x, < 1 2 3 4 > and {min}–{max} of {total}"
      const pageAmount = Math.ceil(count / rowsPerPage);
      return (
        <Box display="flex" justifyContent="center" alignItems="center" gap="37px">
          {setRowsPerPage && (
            <Box display="flex" gap="8px" alignItems="center">
              <Typography>{labels.rowsPerPage}</Typography>
              <Box>
                <SelectField
                  // name="rows"
                  sx={{ height: '32px' }}
                  onChange={(value) => setRowsPerPage(value)}
                  value={rowsPerPage.toString()}
                  options={[10, 25, 50].map((row) => ({
                    value: row,
                    label: row.toString(),
                  }))}
                />
              </Box>
            </Box>
          )}
          <Pagination
            count={pageAmount}
            shape="rounded"
            sx={{
              '& .MuiPagination-ul': {
                borderRadius: '4px',
                border: `1px solid ${COLORS.gray[1000]}`,
              },
              '& .MuiPagination-ul > li > button': {
                margin: '0px',
                borderRadius: '0px',
                color: COLORS.gray[1100],
                borderLeft: `1px solid ${COLORS.gray[1000]}`,
              },
              '& .MuiPagination-ul > li > button:hover': {
                backgroundColor: COLORS.gray[400],
              },
              '& .MuiPagination-ul > :first-of-type > :first-of-type': {
                borderLeft: '0px !important',
              },
              '& .Mui-selected': {
                backgroundColor: `${COLORS.gray[1100]} !important`,
                color: `${COLORS.brand.white} !important`,
              },
              '& .MuiPaginationItem-ellipsis': {
                color: COLORS.gray[1100],
                margin: '0px',
                borderLeft: `1px solid ${COLORS.gray[1000]}`,
                borderRadius: '0px',
                height: '32px',
              },
            }}
            page={page + 1} // Because in the backend it starts with zero.
            onChange={(e, newPage) => {
              !isLoading && handlePageChange(e, newPage - 1); // Because in the backend it starts with zero.
            }}
          />
          <Typography>
            {page * rowsPerPage + 1} - {Math.min((page + 1) * rowsPerPage, count)} {labels.ofAmount} {count}
          </Typography>
        </Box>
      );
    } else {
      // If !showPages, it returns a paginator with {min}–{max} of {total} and < >
      return (
        <TablePagination
          component="div"
          count={count}
          onPageChange={(e, newPage) => {
            !isLoading && handlePageChange(e, newPage);
          }}
          rowsPerPageOptions={rowsPerPageOptions}
          onRowsPerPageChange={(e) => {
            setRowsPerPage && setRowsPerPage(parseInt(e.target.value, 10));
          }}
          page={page}
          rowsPerPage={rowsPerPage}
        />
      );
    }
  }

  // If paginationSettings has "hasMore", it returns a paginator with < >
  // InfinitePaginationSettings
  const {
    rowsPerPage = DEFAULT_ROWS_PER_PAGE,
    page,
    handlePageChange,
    hasMore,
    firstPage = 0,
  } = paginationSettings as InfinitePaginationSettings;
  return (
    <TablePagination
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        padding: 0,
        '& .MuiToolbar-root': {
          padding: 0,
        },
      }}
      count={-1}
      component="div"
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={(e, newPage) => {
        handlePageChange(e, newPage);
      }}
      rowsPerPageOptions={[10]}
      labelDisplayedRows={({ from, to, count }) => ``}
      ActionsComponent={({ onPageChange }) => (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', padding: 0 }}
        >
          <Box
            sx={{
              height: '36px',
              width: '36px',
              border: `1px solid ${COLORS.gray[900]}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderTopLeftRadius: '4px',
              borderBottomLeftRadius: '4px',
            }}
          >
            <ChevronLeftIcon
              onClick={
                page === firstPage
                  ? undefined
                  : () => {
                      onPageChange(null, page - 1);
                    }
              }
              sx={{
                color: page === firstPage ? COLORS.gray[1000] : COLORS.gray[1200],
                cursor: page === firstPage ? 'initial' : 'pointer',
              }}
            />
          </Box>
          <Box
            sx={{
              height: '36px',
              width: '36px',
              border: `1px solid ${COLORS.gray[900]}`,
              borderTopRightRadius: '4px',
              borderBottomRightRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '-1px',
            }}
          >
            <ChevronRightIcon
              onClick={
                hasMore
                  ? () => {
                      onPageChange(null, page + 1);
                    }
                  : undefined
              }
              sx={{
                color: hasMore ? COLORS.gray[1200] : COLORS.gray[1000],
                cursor: hasMore ? 'pointer' : 'initial',
              }}
            />
          </Box>
        </Box>
      )}
    />
  );
};

const Table = ({
  columns = [],
  data = [],
  paginationSettings = null,
  isLoading = false,
  onClickRow = null,
  emptyMessageTitle = '',
  emptyMessageSubtitle = '',
  emptyMessageIcon,
  minWidth = 650,
  onClickSort,
  sortingField,
  sortingDirection,
  stickyHeaders,
  rowSx,
  hideRowChevron,
  paginationLabels,
}: CustomTableProps) => {
  return (
    <TableContainer component={Paper} sx={stickyHeaders ? { overflowX: 'visible' } : {}}>
      <MUITable sx={{ minWidth }} aria-label="table">
        <TableHead
          sx={{
            backgroundColor: COLORS.gray[400],
            ...(stickyHeaders && {
              position: 'sticky',
              top: '64px',
              zIndex: 1100,
            }),
          }}
        >
          <TableRow>
            {columns.map(({ field, headerName, width, headerAlign = 'left', sortable }) => (
              <TableCell
                key={field || headerName}
                sx={{ color: COLORS.brand.night, fontWeight: 500, fontSize: '16px', lineHeight: '24px' }}
                align={headerAlign}
                width={width}
              >
                {sortable && onClickSort ? (
                  <TableSortLabel
                    active={sortingField === field}
                    direction={sortingDirection}
                    onClick={() => onClickSort(field!)}
                  >
                    {headerName}
                  </TableSortLabel>
                ) : (
                  <>{headerName}</>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                <CircularProgress sx={{ color: COLORS.brand.night }} />
              </TableCell>
            </TableRow>
          ) : data.length > 0 ? (
            data.map((row: any) => (
              <TableRow
                key={row.id}
                onClick={() => {
                  onClickRow && onClickRow(row);
                }}
                sx={{
                  '&:hover': {
                    cursor: onClickRow ? 'pointer' : 'initial',
                    backgroundColor: COLORS.gray[300],
                    '& td:last-of-type': {
                      '& #chevron': {
                        visibility: !!onClickRow ? 'visible' : 'hidden',
                      },
                    },
                  },
                  '& td:last-of-type': {
                    '& #chevron': {
                      visibility: 'hidden',
                    },
                  },
                  ...(rowSx ? rowSx(row) : {}),
                }}
              >
                {columns.map((column, columnIndex) => (
                  <TableCell
                    key={column.field || column.headerName}
                    align="left"
                    sx={{ borderBottom: 'none' }}
                  >
                    {columnIndex === columns.length - 1 && onClickRow && !hideRowChevron ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: column.justifyContentLastColumn || 'space-between',
                        }}
                      >
                        {column.renderCell ? (
                          column.renderCell(row)
                        ) : (
                          <Typography>{column?.field ? row[column.field] : '-'}</Typography>
                        )}
                        <ChevronRightIcon
                          id="chevron"
                          sx={{
                            verticalAlign: 'bottom',
                            ml: 1,
                            color: COLORS.gray[1200],
                            borderRadius: '8px',
                          }}
                        />
                      </div>
                    ) : column.renderCell ? (
                      column.renderCell(row)
                    ) : (
                      <Typography>{column?.field ? row[column.field] : '-'}</Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                sx={{ height: { xs: 'initial', md: '150px' }, ...(emptyMessageIcon && { py: '32px' }) }}
              >
                {emptyMessageTitle && (
                  <Typography
                    textAlign="center"
                    variant="h6"
                    sx={{ color: COLORS.brand.night, fontWeight: 700, fontSize: '20px', lineHeight: '30px' }}
                  >
                    {emptyMessageTitle}
                  </Typography>
                )}
                {emptyMessageSubtitle && (
                  <Typography textAlign="center" variant="h6">
                    {emptyMessageSubtitle}
                  </Typography>
                )}
                {emptyMessageIcon && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: '24px' }}>{emptyMessageIcon}</Box>
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </MUITable>
      <Box margin="28px 0 12px">
        {!!paginationSettings && (
          <PaginationComponent
            paginationSettings={paginationSettings}
            isLoading={isLoading}
            labels={{ ...DEFAULT_PAGINATION_LABELS, ...paginationLabels }}
          />
        )}
      </Box>
    </TableContainer>
  );
};

export default Table;
